import { span, type Mountable } from '@llui/dom';

/**
 * `<StatusPill>` — fact-status chip (canon / pending / draft / hypothetical / belief).
 *
 * Maps to `30-design-system.md §3.3` (semantic colors) and §7.1 (the
 * status overlay rule: "A draft fact's entire row uses the Draft
 * semantic colors"). Use this atom for compact status indication on
 * list rows, queue items, or alongside an entity reference where the
 * full row-level status treatment is too heavy.
 *
 * "canon" intentionally renders as a neutral pill — canon is the
 * default and should not visually compete with the exceptional states
 * (`30 §3.3`: "Default; no special treatment"). Surface a canon pill
 * only when the caller wants the indication to be explicit (a legend,
 * a queue, a debug surface).
 *
 * The text on the pill is the caller's responsibility — usually a
 * translated label (`t('status.pending')`). Microcopy from §6
 * ("Pending" never "Suggestion") is enforced in the i18n catalog, not
 * here.
 */

export type StatusKind = 'canon' | 'pending' | 'draft' | 'hypothetical' | 'belief';

export interface StatusPillOptions {
  kind: StatusKind;
  /** Either a static string or a llui reactive text node (`text(...)`). */
  label: string | Mountable;
  /** Optional leading icon — usually a Lucide icon from the caller. */
  icon?: Mountable;
  /** Forwarded `data-testid`. */
  testid?: string;
}

export function statusPill(opts: StatusPillOptions): Mountable {
  const children: (string | Mountable)[] = [];
  if (opts.icon) children.push(opts.icon);
  children.push(opts.label);
  return span(
    {
      class: 'dl-status-pill',
      'data-kind': opts.kind,
      'data-testid': opts.testid,
    },
    children,
  );
}
